/**
 * W2 — Player FSM. Backward-compat: preserves x/y/hp/isDead/moveBy/
 * takeDamage/applyShield/shieldHits surface. FSM is opt-in via update().
 *
 * IMPORTANT — CLAUDE.md 1.1: 學習與遊戲完全分離。FSM 只模型坦克移動/射擊/
 * 受傷/死亡,不含答題 hook。
 */

import { PLAYER } from '../config';
import { IState, StateMachine } from '../ai/StateMachine';

interface Bounds {
  minX: number;
  maxX: number;
}

export interface PlayerInput {
  /** Horizontal motion this frame (sign = direction, 0 = idle). */
  dx?: number;
  /** Vertical motion this frame (horizontal-mode game uses dy as player axis). */
  dy?: number;
  /** True when the player fired a bullet this frame. */
  firing?: boolean;
}

export type PlayerStateName = 'idle' | 'moving' | 'firing' | 'hit' | 'dead';
export type PlayerStateListener = (from: string | undefined, to: string) => void;

interface PlayerFSMContext {
  player: Player;
  input: PlayerInput;
  nowMs: number;
  sm: StateMachine<PlayerFSMContext>;
}

class IdleState implements IState<PlayerFSMContext> {
  readonly name = 'idle';
  enter(_ctx: PlayerFSMContext): void { /* resting state */ }
  update(ctx: PlayerFSMContext, _dt: number): void {
    const { input, sm, player } = ctx;
    if (player.isDead) { sm.transitionTo('dead'); return; }
    if (input.firing) { sm.transitionTo('firing'); return; }
    if ((input.dx ?? 0) !== 0 || (input.dy ?? 0) !== 0) {
      sm.transitionTo('moving');
    }
  }
  exit(_ctx: PlayerFSMContext): void { /* no-op */ }
}

class MovingState implements IState<PlayerFSMContext> {
  readonly name = 'moving';
  enter(_ctx: PlayerFSMContext): void { /* W5 trail/track FX hook */ }
  update(ctx: PlayerFSMContext, _dt: number): void {
    const { input, sm, player } = ctx;
    if (player.isDead) { sm.transitionTo('dead'); return; }
    if (input.firing) { sm.transitionTo('firing'); return; }
    if ((input.dx ?? 0) === 0 && (input.dy ?? 0) === 0) {
      sm.transitionTo('idle');
    }
  }
  exit(_ctx: PlayerFSMContext): void { /* no-op */ }
}

class FiringState implements IState<PlayerFSMContext> {
  readonly name = 'firing';
  enter(_ctx: PlayerFSMContext): void { /* W5 muzzle-flash hook */ }
  update(ctx: PlayerFSMContext, _dt: number): void {
    const { input, sm, player } = ctx;
    if (player.isDead) { sm.transitionTo('dead'); return; }
    if (!input.firing) {
      if ((input.dx ?? 0) !== 0 || (input.dy ?? 0) !== 0) {
        sm.transitionTo('moving');
      } else {
        sm.transitionTo('idle');
      }
    }
    // firing → firing while still firing is a no-op via SM.transitionTo same-name
  }
  exit(_ctx: PlayerFSMContext): void { /* no-op */ }
}

class HitState implements IState<PlayerFSMContext> {
  readonly name = 'hit';
  enter(_ctx: PlayerFSMContext): void { /* W3 hurt-flash hook */ }
  update(ctx: PlayerFSMContext, _dt: number): void {
    const { sm, player, nowMs } = ctx;
    if (player.isDead) { sm.transitionTo('dead'); return; }
    // Auto-resolve once the invuln window expires. Uses the same
    // lastDamageAtMs takeDamage stamps so the hit window stays consistent.
    if (nowMs - player.lastDamageAtMs >= PLAYER.invulnMs) {
      sm.transitionTo('idle');
    }
  }
  exit(_ctx: PlayerFSMContext): void { /* no-op */ }
}

class DeadState implements IState<PlayerFSMContext> {
  readonly name = 'dead';
  enter(_ctx: PlayerFSMContext): void { /* W5 explosion + slow-mo via onTransition */ }
  update(_ctx: PlayerFSMContext, _dt: number): void {
    /* sticky terminal — scene restart resets the player instance */
  }
  exit(_ctx: PlayerFSMContext): void { /* no-op (terminal) */ }
}

export class Player {
  x: number;
  y: number;
  hp: number = PLAYER.hp;
  shieldHits: number = 0;
  /** Exposed (was private) so FSM HitState can compute invuln expiry. */
  lastDamageAtMs: number = -Infinity;

  private _nowMs: number = 0;
  private readonly _sm: StateMachine<PlayerFSMContext>;
  private readonly _ctx: PlayerFSMContext;
  private readonly _listeners: PlayerStateListener[] = [];
  private readonly _input: PlayerInput = { dx: 0, dy: 0, firing: false };

  constructor(position: { x: number; y: number }) {
    this.x = position.x;
    this.y = position.y;
    this._ctx = {
      player: this,
      input: this._input,
      nowMs: 0,
      sm: null as unknown as StateMachine<PlayerFSMContext>,
    };
    this._sm = new StateMachine<PlayerFSMContext>(this._ctx);
    this._ctx.sm = this._sm;
    this._sm.add(new IdleState());
    this._sm.add(new MovingState());
    this._sm.add(new FiringState());
    this._sm.add(new HitState());
    this._sm.add(new DeadState());
    // Wrap transitionTo to emit listener events on real transitions.
    const orig = this._sm.transitionTo.bind(this._sm);
    this._sm.transitionTo = (name: string) => {
      const from = this._sm.currentName;
      orig(name);
      const to = this._sm.currentName;
      if (to && from !== to) {
        for (const l of this._listeners) l(from, to);
      }
    };
    this._sm.transitionTo('idle');
  }

  get isDead(): boolean { return this.hp <= 0; }
  get state(): PlayerStateName {
    return (this._sm.currentName ?? 'idle') as PlayerStateName;
  }

  onTransition(listener: PlayerStateListener): () => void {
    this._listeners.push(listener);
    return () => {
      const i = this._listeners.indexOf(listener);
      if (i >= 0) this._listeners.splice(i, 1);
    };
  }

  /** Advance FSM by one frame. dtMs in milliseconds (matches scene update). */
  update(dtMs: number, input: PlayerInput = {}): void {
    this._nowMs += dtMs;
    this._input.dx = input.dx ?? 0;
    this._input.dy = input.dy ?? 0;
    this._input.firing = input.firing ?? false;
    this._ctx.nowMs = this._nowMs;
    this._sm.update(dtMs / 1000);
  }

  moveBy(dx: number, bounds: Bounds): void {
    this.x = Math.max(bounds.minX, Math.min(bounds.maxX, this.x + dx));
  }

  applyShield(hits: number): void {
    this.shieldHits = hits;
  }

  takeDamage(amount: number, nowMs: number): void {
    if (this.shieldHits > 0) {
      this.shieldHits--;
      return;
    }
    if (nowMs - this.lastDamageAtMs < PLAYER.invulnMs) return;
    this.hp = Math.max(0, this.hp - amount);
    this.lastDamageAtMs = nowMs;
    // Sync FSM clock to caller clock so HitState.update can compute invuln
    // expiry against the same nowMs the scene uses (otherwise _nowMs lags).
    this._nowMs = nowMs;
    this._ctx.nowMs = nowMs;
    if (this.isDead) {
      this._sm.transitionTo('dead');
    } else {
      this._sm.transitionTo('hit');
    }
  }
}
